import { Component } from '@theme/component';

/**
 * A custom element that manages tab switching for the redesigned mobile menu drawer - one tab
 * per top-level nav item, each controlling a single visible content panel underneath.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} tab - The tab trigger buttons.
 * @property {HTMLElement[]} panel - The tab content panels.
 *
 * @extends {Component<Refs>}
 */
class MobileMenuTabs extends Component {
  requiredRefs = ['tab', 'panel'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
  }

  /**
   * Activate the tab that was clicked. `on:click` is dispatched through a single document-level
   * listener (see `@theme/component`), so `event.currentTarget` is always `document` here, not
   * the button - the actual tab has to be found from `event.target` instead.
   * @param {Event} event
   */
  activate(event) {
    if (!(event.target instanceof Element)) return;

    const tab = event.target.closest('[role="tab"]');
    if (!(tab instanceof HTMLElement)) return;

    this.#activateTab(tab);
  }

  /**
   * Move focus and activation between tabs with the arrow/Home/End keys, per the WAI-ARIA
   * tabs pattern's "automatic activation" model.
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    const tabs = this.refs.tab;
    const currentIndex = tabs.findIndex((tab) => tab === document.activeElement);
    if (currentIndex === -1) return;

    let nextIndex;
    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const nextTab = tabs[nextIndex];
    if (!nextTab) return;

    nextTab.focus();
    this.#activateTab(nextTab);
  };

  /**
   * @param {HTMLElement} tab
   */
  #activateTab(tab) {
    const targetId = tab.getAttribute('aria-controls');

    this.refs.tab.forEach((otherTab) => {
      const selected = otherTab === tab;
      otherTab.setAttribute('aria-selected', String(selected));
      otherTab.tabIndex = selected ? 0 : -1;
    });

    this.refs.panel.forEach((panel) => {
      panel.hidden = panel.id !== targetId;
    });
  }
}

if (!customElements.get('mobile-menu-tabs')) {
  customElements.define('mobile-menu-tabs', MobileMenuTabs);
}
